import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import filestates from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {AA_SEQUENCE_IMPORT_MODAL_TAB} from 'molbio/MolBioConstants';
import ProteinActions from 'molbio/proteins/ProteinActions';
import ProteinDAO, {ImportProteinResponse} from 'molbio/proteins/ProteinDAO';
import {PROTEIN_DEFAULT_DATABASE_IMPORT_PREFERENCE_KEY} from 'molbio/proteins/util/ProteinConstants';
import notifications, {FOREVER_DURATION} from 'notifications';
import preferences from 'Preferences';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {SortKey} from 'search/SearchConstants';
import {filterTagSchemasGraphQL} from 'tag-schema/filterTagSchemas';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import FileChooser from 'ui-library/FileChooser';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import * as ImportUtil from 'util/ImportUtil';
import {InputChangeEvent, Optional} from 'util/Types';

const IMPORT_PROTEIN_MODAL_KEY = 'IMPORT_PROTEIN_MODAL_KEY';

function closeImportProteinModal(): void {
  ModalContainer.close(IMPORT_PROTEIN_MODAL_KEY);
}

export function openImportProteinModal(props: Omit<ImportProteinModalProps, 'onRequestClose'>): void {
  ModalContainer.open(
    IMPORT_PROTEIN_MODAL_KEY,
    <ImportProteinModal {...props} onRequestClose={closeImportProteinModal} />
  );
}

const trackImportFail = (importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB, error: string): void => {
  analytics.track('protein', 'import_fail', {
    product: ANALYTICS_MODULE.MOLBIO,
    importFlow,
    error,
  });
};

// Called when the import task ends (though the import itself may have failed)
// taskId - the TaskTray id corresponding to this protein file import
// results - list of protein dicts
// error - nullable string describing the error if parsing the file failed
// warningMessage - either null or a string describing a warning to give. This may happen in either
//   the success or error cases.
const _proteinImportFinished = (
  taskId: string,
  {
    results: maybeResults,
    error,
    warningMessage,
  }:
    | ImportProteinResponse
    | {
        // This additional case is for the "upload protein file failed" case. See the `_proteinImportFinished`
        // call in `_handleStartProteinUpload` below.
        results?: never;
        error: string;
        warningMessage?: never;
      }
): void => {
  if (warningMessage) {
    notifications.warn(warningMessage, {duration: FOREVER_DURATION});
  }

  if (error == null) {
    analytics.track('protein', 'import_success', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE,
    });
    const results = maybeResults!;
    const numProteins = results.length;
    const {folder} = results[0];
    const onTaskClick = (): void => {
      if (results.length === 1) {
        FileBrowserActions.openFile(results[0].id);
      } else {
        filestates.get().openFolder(folder.api_identifier, folder.url, {
          reverse: '1',
          section: FileBrowserConstants.INVENTORY_SECTION,
          sort: SortKey.SORT_MODIFIED_AT_KEY,
        });
      }
      TrayTaskActions.removeTrayTask(taskId);
    };

    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      subheading: `Imported ${numProteins} ${benchlingUtil.pluralize(numProteins, 'AA sequence')} to ${
        folder.name
      }`,
      onHeadingClick: onTaskClick,
      onSubheadingClick: onTaskClick,
    });
  } else {
    trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.FILE, error);
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      subheading: 'AA Sequence Import failed',
      error,
    });
  }
};

const importProteinFromLocation = (
  folderId: string,
  tagSchemaId: string | undefined,
  location: string,
  taskId: string
): JQueryPromise<unknown> =>
  ProteinDAO.importProtein({location, folderId, tagSchemaId})
    .then(_proteinImportFinished.bind(null, taskId))
    .done(() => {
      FolderDAOCache.invalidateFolderContents(folderId);
    })
    .fail(() => {
      const message = 'Uploading AA sequence file failed - please try again';
      trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.FILE, message);
      // For some reason, we weren't able to start this task
      TrayTaskActions.updateTrayTask(taskId, {
        progress: TrayTaskConstants.FAILURE_PROGRESS,
        error: message,
      });
    });

interface ImportProteinModalProps {
  initialFolderId?: string;
  onRequestClose: () => void;
}

interface ImportProteinModalState {
  folderId: Optional<string>;
  proteinFiles: Array<File>;
  tagSchemaId?: string;
  accessions: string;
  database: string;
  activeTab: AA_SEQUENCE_IMPORT_MODAL_TAB;
}

export default class ImportProteinModal extends React.Component<
  ImportProteinModalProps,
  ImportProteinModalState
> {
  state: ImportProteinModalState = {
    folderId: this.props.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY),
    proteinFiles: [],
    tagSchemaId: undefined,
    // Comma-separated list of accessions, ignoring whitespace
    accessions: '',
    database: preferences.get(PROTEIN_DEFAULT_DATABASE_IMPORT_PREFERENCE_KEY) || 'NCBI',
    activeTab: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE,
  };

  handleClickTab = (tab: AA_SEQUENCE_IMPORT_MODAL_TAB): void => {
    this.setState({activeTab: tab});
    analytics.track('protein', 'import_tab_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: tab,
    });
  };

  _handleChangeTagSchemaId = (tagSchema: {id: string} | null): void => {
    assert(tagSchema != null, 'TagSchemaSelector returned null for tagSchema');
    this.setState({tagSchemaId: tagSchema.id});
  };

  _handleStartProteinUpload = (): void => {
    analytics.track('protein', 'import_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE,
    });
    this.state.proteinFiles.forEach((proteinFile) => {
      const taskId = TrayTaskActions.createTrayTask({
        heading: proteinFile.name,
      });
      ImportUtil.upload(proteinFile)
        .progress((progress: number) => {
          TrayTaskActions.updateTrayTask(taskId, {progress});
        })
        .then((s3location: string) => {
          importProteinFromLocation(this.state.folderId!, this.state.tagSchemaId, s3location, taskId);
        })
        .fail((res: Response) => {
          const error = notifications.getFriendlyError(res) ?? GENERIC_ERROR_MESSAGE;
          trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.FILE, error);
          _proteinImportFinished(taskId, {error});
        });
    });
    this.props.onRequestClose();
  };

  handleFilesSelected = (...files: Array<File>): void => {
    analytics.track('protein', 'select_files', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE,
    });
    this.setState({proteinFiles: [...this.state.proteinFiles, ...files]});
  };

  _handleSetFolderId = (e: {target: {value: string}}): void => {
    const folderId = e.target.value;
    this.setState({folderId});
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
  };

  _handleSetAccessions = (e: InputChangeEvent): void => {
    if (!this.state.accessions) {
      analytics.track('protein', 'select_files', {
        product: ANALYTICS_MODULE.MOLBIO,
        importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.DB,
      });
    }
    const accessions = e.target.value;
    this.setState({accessions});
  };

  _handleSetDatabase = (database: string): void => {
    this.setState({database});
  };

  _getAccessionList = (): Array<string> =>
    this.state.accessions
      .split(',')
      .map((accession) => accession.trim())
      .filter((accession) => accession !== '');

  _handleDBImportProtein = (): void => {
    analytics.track('protein', 'import_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.DB,
    });
    const accessionList = this._getAccessionList();
    if (accessionList.length > 20) {
      const error = 'Please import no more than 20 AA sequences at a time.';
      trackImportFail(AA_SEQUENCE_IMPORT_MODAL_TAB.DB, error);
      notifications.warn(error);
    } else {
      for (const accession of this._getAccessionList()) {
        ProteinActions.dbImportProtein({
          accession,
          database: this.state.database,
          folderId: this.state.folderId!,
        });
      }
      this.props.onRequestClose();
    }
  };

  renderFolderSelector = (): JSX.Element => (
    <FormGroup>
      <Label>{DEFAULT_PROJECT_FOLDER_LABEL_TEXT}</Label>
      <FolderSelector
        showInventory={true}
        value={this.state.folderId}
        onChange={this._handleSetFolderId}
        unsetOnFetchFail={true}
      />
    </FormGroup>
  );

  // Buttons are rendered in the tabs because we don't know which tab is active.
  renderButtons = ({
    importOnClick,
    importDisabled,
  }: {
    importOnClick: () => void;
    importDisabled: boolean;
  }): JSX.Element => (
    <div className="modal-footer importProteinModal-modalBodyFooter">
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        onClick={this.props.onRequestClose}
        size={ButtonSize.MEDIUM}
        text="Cancel"
      />
      <Button
        buttonStyle={ButtonStyle.PRIMARY}
        onClick={importOnClick}
        disabled={importDisabled}
        size={ButtonSize.MEDIUM}
        text="Import"
      />
    </div>
  );

  renderUploadFilesTab = ({shouldShowTagSchemas}: {shouldShowTagSchemas: boolean}): JSX.Element => (
    <div>
      <p>We currently support FASTA, GenPept, Uniprot XML, and Vector NTI .pa4 files.</p>
      {this.renderFolderSelector()}
      {shouldShowTagSchemas && (
        <div className="u-moderate-spacing-bottom">
          <Label>Schema</Label>
          <TagSchemaSelector
            selectedTagSchemaId={this.state.tagSchemaId}
            onChange={this._handleChangeTagSchemaId}
            oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
          />
        </div>
      )}
      <Label>AA sequence files</Label>
      {this.state.proteinFiles.map((proteinFile) => (
        <p className="help-block" key={proteinFile.name}>
          {proteinFile.name}
        </p>
      ))}
      <FileChooser onSelectFiles={this.handleFilesSelected} multiple={true} />
      {this.renderButtons({
        importOnClick: this._handleStartProteinUpload,
        importDisabled: this.state.proteinFiles.length === 0 || this.state.folderId == null,
      })}
    </div>
  );

  renderImportFromDatabaseTab = (): JSX.Element => (
    <div>
      <Row>
        <Col md={9}>
          <FormGroup>
            <Label>Accessions</Label>
            <FormInput
              placeholder="e.g., P68871, NP_000509, Q9TSP1"
              value={this.state.accessions}
              onChange={this._handleSetAccessions}
            />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <Label>Database</Label>
            <Dropdown
              menu={
                <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL}>
                  {['NCBI', 'UniProt'].map((db) => (
                    <Option
                      key={db}
                      onSelect={() => {
                        this._handleSetDatabase(db);
                      }}
                    >
                      {db}
                    </Option>
                  ))}
                </Menu>
              }
            >
              <Button
                isDropdown={true}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                text={this.state.database}
              />
            </Dropdown>
          </FormGroup>
        </Col>
      </Row>
      {this.renderFolderSelector()}
      {this.renderButtons({
        importOnClick: this._handleDBImportProtein,
        importDisabled: this._getAccessionList().length === 0 || this.state.folderId == null,
      })}
    </div>
  );

  render(): JSX.Element {
    return (
      // We need to fetch all `TagSchema`s to determine if we should show the `TagSchemaSelector`, based on if
      // there are any protein schemas to choose from. Ideally this would be a simpler query, but for now, we
      // use the shared `ALL_TAG_SCHEMAS_QUERY` to benefit from shared caching.
      <BenchlingQuery<AllTagSchemasQuery>
        query={ALL_TAG_SCHEMAS_QUERY}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      >
        {(data) => (
          <Modal
            title="Import AA sequence"
            showHeaderBorder={false}
            enableRedesign={true}
            onRequestClose={this.props.onRequestClose}
          >
            <TabContainer activeTab={this.state.activeTab} onRequestTabChange={this.handleClickTab}>
              <Tab tabKey={AA_SEQUENCE_IMPORT_MODAL_TAB.FILE} title="Upload files">
                {this.renderUploadFilesTab({
                  shouldShowTagSchemas:
                    filterTagSchemasGraphQL(data.tagSchemas, {
                      oligoAwareFolderItemTypes: [ModelTypeConstants.PROTEIN],
                    }).length > 0,
                })}
              </Tab>
              <Tab tabKey={AA_SEQUENCE_IMPORT_MODAL_TAB.DB} title="Import from database">
                {this.renderImportFromDatabaseTab()}
              </Tab>
            </TabContainer>
          </Modal>
        )}
      </BenchlingQuery>
    );
  }
}
